import { useEffect, useState, useContext, useRef } from "react"
import { DateTime } from "luxon"
import NoData from "../NoData"
import { ReactComponent as BoxMessageIcon } from "./resources/empty_box.svg"
import markAsReadIcon from "./resources/mark_as_read_icon.svg"
import markAsUnreadIcon from "./resources/mark_as_unread_icon.svg"

//Notifications modules
import {
  noteApiListNotificationsPOST,
  noteApiMarkAsReadPut,
  noteApiMarkAsUnreadPut,
  noteApiListEventTypesGet,
  noteApiMarkSystemAsReadPut,
  noteApiMarkSystemAsUnreadPut,
  noteApiMarkAllAsReadPut,
  noteApiMarkAllSystemAsReadPut,
} from "./notificationsApi"

import { LoaderMedium } from "../Loaders"

//styles
import noteStyle from "../../styles/NotificationsForm.module.css"

// Context
import { UserContext } from "../../context/userContext"

//prime react
import { Paginator } from "primereact/paginator"
import { Dialog } from "primereact/dialog"
import { Button } from "primereact/button"
import { Tag } from "primereact/tag"
import { Tooltip } from "primereact/tooltip"
import { Accordion, AccordionTab } from "primereact/accordion"
import { Badge } from "primereact/badge"

//Helpers
import {
  anyNotificationHasTags,
  notificationHasTags,
  replaceNotificationTags,
} from "./notificationsTags"
import { useLazyQuery } from "@apollo/client"
import { GET_NOTIFICATION_USERS } from "./notificationsQueries"
import { classNames } from "primereact/utils"
import CustomFilter from "../CustomFilter/CustomFilter"
import {
  FilterDefaults,
  FilterLocation,
  FilterTypes,
} from "../CustomFilter/FilterConstants"
import StandardPageHeader from "../StandardPageHeader/StandardPageHeader"
import { LogError } from "../../helpers/logger"
import { getMicroserviceStaffId } from "../../config/microservicesSystemUsers"

const NotificationsDialog = ({
  visible,
  setVisible,
  organizations,
  onShowError,
  onHandleActionURL,
  onRefreshBellCounter,
}) => {
  const userCtx = useContext(UserContext)
  const notificationServiceStaffId = getMicroserviceStaffId() //Service staff account: RecoveryLInk Microservices
  const [pageSize, setPageSize] = useState(10)
  const [loading, setLoading] = useState(false)
  const [notifications, setNotifications] = useState(null)
  const [eventTypes, setEventTypes] = useState([])
  const [lastRefresh, setLastRefresh] = useState(null)
  const [refreshNotifications, setRefreshNotifications] = useState(null)
  const [activeNotificationIndex, setActiveNotificationIndex] = useState(null)
  const [markAllReadConfirmed, setMarkAllReadConfirmed] = useState(false)
  const readUnreadUpdating = useRef([])
  const currentPage = 1
  const [notificationFilters, setNotificationFilters] = useState([])
  const [notificationSorting, setNotificationSorting] = useState([])
  const [organizationOptions, setOrganizationOptions] = useState([])

  // Get event types
  useEffect(() => {
    GetEventTypes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Get organizations
  useEffect(() => {
    if (organizations) {
      const organizationOptions = organizations?.map((org) => {
        return {
          name: org.organization_name,
          code: org.organization_id,
        }
      })
      setOrganizationOptions(organizationOptions)
    }
  }, [organizations])

  // Define filters and sorting
  useEffect(() => {
    if (
      organizations?.length > 0 &&
      eventTypes?.length > 0 &&
      notificationFilters?.length === 0
    ) {
      // Set filter options
      setNotificationFilters([
        // Bar filters
        {
          key: "organization_filter",
          label: "Organization",
          defaultValue: FilterDefaults.multiselect,
          currentValue: FilterDefaults.multiselect,
          type: FilterTypes.multiselect,
          location: FilterLocation.bar,
          options: organizationOptions,
          placeholder: "Select organization",
          showClear: false,
          showFilter: false,
        },
        // Panel filters
        {
          key: "text_search",
          label: "Event title or description",
          placeholder: "Search",
          defaultValue: FilterDefaults.text,
          currentValue: FilterDefaults.text,
          type: FilterTypes.text,
          location: FilterLocation.panel,
        },
        {
          key: "event_type_filter",
          label: "Event type",
          defaultValue: FilterDefaults.dropdown,
          currentValue: FilterDefaults.dropdown,
          type: FilterTypes.dropdown,
          location: FilterLocation.panel,
          options: eventTypes,
          placeholder: "Select",
          showClear: true,
          showFilter: true,
        },
        {
          key: "created_date_filter",
          label: "Created date",
          defaultValue: FilterDefaults.calendar,
          currentValue: FilterDefaults.calendar,
          type: FilterTypes.calendar,
          location: FilterLocation.panel,
          placeholder: "Select",
          showTime: false,
          showIcon: false,
          rangeSelect: true,
        },
        {
          key: "read_date_filter",
          label: "Read date",
          defaultValue: FilterDefaults.calendar,
          currentValue: FilterDefaults.calendar,
          type: FilterTypes.calendar,
          location: FilterLocation.panel,
          placeholder: "Select",
          showTime: false,
          showIcon: false,
          rangeSelect: true,
        },
        {
          key: "include_unread_notifications",
          label: "Include unread notifications",
          defaultValue: true,
          currentValue: true,
          type: FilterTypes.checkbox,
          location: FilterLocation.panel,
        },
        {
          key: "include_read_notifications",
          label: "Include read notifications",
          defaultValue: false,
          currentValue: false,
          type: FilterTypes.checkbox,
          location: FilterLocation.panel,
        },
      ])

      // Set sort options
      setNotificationSorting([
        {
          name: "Created",
          code: "CreatedAt",
          direction: "DESC",
          enabled: true,
        },
        { name: "Read", code: "ReadAt", direction: "ASC", enabled: false },
      ])

      if (!notifications || notifications.items?.length === 0) {
        RefreshNotifications()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationOptions, eventTypes])

  useEffect(() => {
    if (refreshNotifications > lastRefresh) {
      GetNotifications()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNotifications])

  const [getNotificationUsers] = useLazyQuery(GET_NOTIFICATION_USERS, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: notificationServiceStaffId, //Service staff account: RecoveryLInk Microservices
      organization_id: "", // This will be set dynamically later
    },
  })

  function getFilterByKey(key) {
    const filter = notificationFilters.find((f) => f.key === key)
    return filter
  }

  function RefreshNotifications() {
    setRefreshNotifications(new Date().getTime())
  }

  function combineReadAndUnreadFilters(includeUnread, includeRead) {
    if (includeRead.currentValue && includeUnread.currentValue) return null
    else if (includeRead.currentValue && !includeUnread.currentValue)
      return true
    else return false
  }

  async function GetNotifications(page, currentPageSize, showLoading = true) {
    // Limit refreshes
    if (lastRefresh !== null && new Date().getTime() - lastRefresh < 100) return
    setLastRefresh(new Date().getTime())

    showLoading && setLoading(true)

    const organization_filter = getFilterByKey("organization_filter")
    const event_type_filter = getFilterByKey("event_type_filter")
    const created_date_filter = getFilterByKey("created_date_filter")
    const read_date_filter = getFilterByKey("read_date_filter")
    const include_unread_notifications = getFilterByKey(
      "include_unread_notifications",
    )
    const include_read_notifications = getFilterByKey(
      "include_read_notifications",
    )

    try {
      const requestPayload = {
        jwt: userCtx.tokens.getNotificationToken(),
        userId: userCtx.participant_id,
        userAlias: userCtx.participant_alias,
        organizationIds:
          organization_filter.currentValue.length > 0
            ? organization_filter.currentValue
            : userCtx.participant_organizations.map((o) => o.organization_id),
        readFilter: combineReadAndUnreadFilters(
          include_unread_notifications,
          include_read_notifications,
        ),
        eventTypeFilter: event_type_filter.currentValue,
        createDateStartFilter: created_date_filter.currentValue
          ? created_date_filter.currentValue[0]
          : null,
        createDateEndFilter: created_date_filter.currentValue
          ? created_date_filter.currentValue[1]
          : null,
        readDateStartFilter: read_date_filter.currentValue
          ? read_date_filter.currentValue[0]
          : null,
        readDateEndFilter: read_date_filter.currentValue
          ? read_date_filter.currentValue[1]
          : null,
        titleFilter: getFilterByKey("text_search").currentValue,
        sorting: notificationSorting
          .filter((s) => s.enabled)
          .map((s) => {
            return {
              sortColumn: s.code,
              sortDirection: s.direction,
            }
          }),
        pageNum: page || currentPage,
        pageSize: currentPageSize || pageSize,
      }

      // Add organization alias values to the requestPayload.organizationIds
      if (
        requestPayload.organizationIds.length > 0 &&
        requestPayload.organizationIds[0] !== "All"
      ) {
        const alternateOrganizationIds = requestPayload.organizationIds.map(
          (orgId) => {
            const org = organizations.find((o) => o.organization_id === orgId)
            return org ? org.organization_alias : orgId
          },
        )

        requestPayload.organizationIds = requestPayload.organizationIds.concat(
          alternateOrganizationIds,
        )
      }

      const notificationsResult =
        await noteApiListNotificationsPOST(requestPayload)

      if (notificationsResult) {
        if (anyNotificationHasTags(notificationsResult.items)) {
          // Get staff list for tag replacement.
          // Fetch per organization and combine results
          let staffList = []
          for (const organization of userCtx.participant_organizations) {
            const result = await getNotificationUsers({
              variables: {
                staff_id: notificationServiceStaffId,
                organization_id: organization.organization_id,
              },
            })

            if (result.data && result.data.staff) {
              staffList = staffList.concat(result.data.staff)
            }
          }

          let participantList = [
            {
              participant_id: userCtx.participant_id,
              participant_first_name: userCtx.participant_first_name,
              participant_last_name: userCtx.participant_last_name,
            },
          ]

          notificationsResult.items = await Promise.all(
            notificationsResult.items.map(async (notification) => {
              if (notificationHasTags(notification)) {
                return await replaceNotificationTags(
                  notification,
                  participantList,
                  undefined,
                  undefined,
                  staffList,
                  undefined,
                )
              } else {
                return notification
              }
            }),
          )
        }

        const _notifications = {
          pageSize: currentPageSize || pageSize,
          pageNum: page || currentPage,
          numTotal: notificationsResult.numTotal,
          items: notificationsResult.items,
        }

        // Add description expanded property to track expansion state for long descriptions
        _notifications.items.forEach((n) => {
          n.descrExpanded = false
        })

        setNotifications(_notifications)
        onRefreshBellCounter(new Date().getTime())
      }
    } catch (error) {
      LogError("Unable to list notifications", error)
      onShowError("Unable to list notifications. See console for details.")
    } finally {
      setLoading(false)
    }
  }

  async function markAsRead(notificationId, systemNotification) {
    try {
      readUnreadUpdating.current = [
        ...readUnreadUpdating.current,
        notificationId,
      ]

      if (!systemNotification) {
        await noteApiMarkAsReadPut(
          userCtx.tokens.getNotificationToken(),
          notificationId,
        )
      } else {
        await noteApiMarkSystemAsReadPut(
          userCtx.tokens.getNotificationToken(),
          notificationId,
          userCtx.participant_id,
        )
      }
      onRefreshBellCounter(new Date().getTime())

      await GetNotifications(null, null, false)
      readUnreadUpdating.current = readUnreadUpdating.current.filter(
        (item) => item !== notificationId,
      )
    } catch (error) {
      readUnreadUpdating.current = readUnreadUpdating.current.filter(
        (item) => item !== notificationId,
      )

      LogError(
        `Unable to mark notification as read (id: ${notificationId})`,
        error,
      )
      onShowError(
        `Unable to mark notification as read (id: ${notificationId}). See console for details.`,
      )
    }
  }

  async function markAllAsRead() {
    try {
      setLoading(true)

      await noteApiMarkAllAsReadPut(
        userCtx.tokens.getNotificationToken(),
        userCtx,
      )
      await noteApiMarkAllSystemAsReadPut(
        userCtx.tokens.getNotificationToken(),
        userCtx,
      )

      onRefreshBellCounter(new Date().getTime())

      await GetNotifications()
      setLoading(false)
    } catch (error) {
      setLoading(false)
      LogError(`Unable to mark all notifications as read`, error)
      onShowError(
        `Unable to mark all notifications as read. See console for details.`,
      )
    }
  }

  async function markAsUnread(notificationId, systemNotification) {
    try {
      readUnreadUpdating.current = [
        ...readUnreadUpdating.current,
        notificationId,
      ]

      if (!systemNotification) {
        await noteApiMarkAsUnreadPut(
          userCtx.tokens.getNotificationToken(),
          notificationId,
        )
      } else {
        await noteApiMarkSystemAsUnreadPut(
          userCtx.tokens.getNotificationToken(),
          notificationId,
          userCtx.participant_id,
        )
      }
      onRefreshBellCounter(new Date().getTime())

      await GetNotifications(null, null, false)
      readUnreadUpdating.current = readUnreadUpdating.current.filter(
        (item) => item !== notificationId,
      )
    } catch (error) {
      readUnreadUpdating.current = readUnreadUpdating.current.filter(
        (item) => item !== notificationId,
      )
      LogError(
        `Unable to mark notification as unread (id: ${notificationId})`,
        error,
      )
      onShowError(
        `Unable to mark notification as unread (id: ${notificationId}). See console for details.`,
      )
    }
  }

  async function GetEventTypes() {
    setLoading(true)
    try {
      const res = await noteApiListEventTypesGet(
        userCtx.tokens.getNotificationToken(),
      )
      if (res) {
        const eventTypeOptions = res.map((e) => {
          return {
            name: e.friendlyName,
            code: e.name,
          }
        })
        setEventTypes(eventTypeOptions)
      }
    } catch (error) {
      LogError("Unable to list event types", error)
      onShowError("Unable to list event types. See console for details.")
    }
    setLoading(false)
  }

  const getOrganization = (organization_id) => {
    if (organization_id !== "0") {
      let org = organizations.filter(
        (x) => x.organization_id === organization_id,
      )

      if (org.length > 0) {
        return org[0].organization_name
      } else {
        org = organizations.filter(
          (x) => x.organization_alias === organization_id,
        )
        if (org.length > 0) {
          return org[0].organization_name
        } else {
          return "Unknown organization"
        }
      }
    } else {
      console.log("Organization ID is 0")
      return ""
    }
  }

  const notifDescrStripped = (descr) => {
    return descr.replace(/<[^>]+>/g, "")
  }

  const itemHeader = (data) => {
    const _notifDescrStripped = notifDescrStripped(data.description)

    return (
      <div className={noteStyle.noteHeaderGridContainer}>
        <Tooltip target=".header-disabled-tt" />
        <div
          className={noteStyle.unread}
          style={data.read ? { visibility: "hidden" } : {}}
        />
        <div className={noteStyle.noteHeaderTitleDesc}>
          <div className={noteStyle.noteHeaderGridItemTitle}>
            {data.title}
            {data.highPriority && (
              <Badge
                value="HIGH PRIORITY"
                className={noteStyle.noteHighImportant}
                severity="danger"
              />
            )}
          </div>
          <div className={noteStyle.noteHeaderGridItemDescr}>
            {!data.descrExpanded ? (
              <>
                <div style={{ whiteSpace: "pre-wrap" }}>
                  {_notifDescrStripped.length > 160
                    ? _notifDescrStripped.substring(0, 157) + "..."
                    : data.description}
                </div>
                {_notifDescrStripped.length > 160 && (
                  <Button
                    className={classNames(
                      noteStyle.noteMarkAsReadBtn,
                      noteStyle.toggleDescriptionButton,
                    )}
                    icon="pi pi-angle-down"
                    iconPos="right"
                    severity="secondary"
                    onClick={() => {
                      setNotifications((prev) => ({
                        ...prev,
                        items: prev.items.map((n) =>
                          n.id === data.id ? { ...n, descrExpanded: true } : n,
                        ),
                      }))
                    }}
                  />
                )}
              </>
            ) : (
              <div style={{ whiteSpace: "pre-wrap" }}>
                {data.description}
                <Button
                  className={classNames(
                    noteStyle.noteMarkAsReadBtn,
                    noteStyle.toggleDescriptionButton,
                  )}
                  icon="pi pi-angle-up"
                  iconPos="right"
                  severity="secondary"
                  onClick={() => {
                    setNotifications((prev) => ({
                      ...prev,
                      items: prev.items.map((n) =>
                        n.id === data.id ? { ...n, descrExpanded: false } : n,
                      ),
                    }))
                  }}
                />
              </div>
            )}
          </div>
        </div>
        {data.systemNotification && (
          <Tag
            value="system notification"
            className={noteStyle.systemNot}
            severity="info"
          />
        )}
        <div className={noteStyle.noteHeaderOther}>
          {!data.read ? (
            <Button
              className={noteStyle.noteMarkAsReadBtn}
              onClick={(e) => {
                if (!data.systemNotification) e.stopPropagation()
                markAsRead(data.id, data.systemNotification)
              }}
              disabled={readUnreadUpdating.current.includes(data.id)}
              loading={readUnreadUpdating.current.includes(data.id)}
            >
              {!readUnreadUpdating.current.includes(data.id) && (
                <img src={markAsReadIcon} alt="mark as read" />
              )}{" "}
              Mark read
            </Button>
          ) : (
            <Button
              className={noteStyle.noteMarkAsUnreadReadBtn}
              onClick={(e) => {
                if (!data.systemNotification) e.stopPropagation()
                markAsUnread(data.id, data.systemNotification)
              }}
              disabled={readUnreadUpdating.current.includes(data.id)}
              loading={readUnreadUpdating.current.includes(data.id)}
            >
              {!readUnreadUpdating.current.includes(data.id) && (
                <img src={markAsUnreadIcon} alt="mark unread" />
              )}{" "}
              Mark unread
            </Button>
          )}
          <div>
            <div className={noteStyle.noteHeaderGridItemCreation}>
              <i
                className="pi pi-clock"
                style={{
                  fontSize: "0.75rem",
                  lineHeight: "0.75rem",
                  marginRight: "5px",
                }}
              />
              {DateTime.fromISO(data.createdAt, { zone: "utc" })
                .setZone("local")
                .toLocaleString(DateTime.DATETIME_SHORT)}{" "}
              <br />
            </div>
            <div className={noteStyle.noteHeaderGridItemOrg}>
              {getOrganization(data.groupId)}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const actionButton = (label, url, id, notification) => {
    return (
      <>
        {url ? (
          <div
            className={`${userCtx.isParticipantInactive(notification.organizationId) ? "action-disabled-tt" : ""}`}
            data-pr-tooltip={
              userCtx.isParticipantInactive(notification.organizationId)
                ? "Inactive participant"
                : ""
            }
            data-pr-position="bottom"
          >
            <Button
              className={`p-button-rounded p-button-secondary ${noteStyle.noteBodyActionButton}`}
              onClick={() => {
                onHandleActionURL(url, id, notification)
                setVisible(false)
              }}
              disabled={userCtx.isParticipantInactive(
                notification.organizationId,
              )}
              label={label}
            />
          </div>
        ) : null}
      </>
    )
  }

  const itemTemplate = (data) => {
    return (
      <AccordionTab
        headerTemplate={itemHeader(data)}
        headerStyle={{ width: "100%" }}
        key={data.id}
        className={data.systemNotification ? noteStyle.systemNotification : ""}
      >
        <div className={noteStyle.noteBodyContainer}>
          <div className={noteStyle.noteBodyInfo}>
            <div>
              <span className={classNames(noteStyle.label, "mr-1")}>
                Last modified:
              </span>
              {DateTime.fromISO(data.modifiedAt, { zone: "utc" })
                .setZone("local")
                .toLocaleString(DateTime.DATETIME_SHORT)}
            </div>
            <div>
              <label className={classNames(noteStyle.label, "mr-1")}>
                Read on:
              </label>
              {data.read
                ? DateTime.fromISO(data.readAt, { zone: "utc" })
                    .setZone("local")
                    .toLocaleString(DateTime.DATETIME_SHORT)
                : "Unread"}
            </div>
          </div>
          <Tooltip target=".action-disabled-tt" />
          <div className={noteStyle.noteBodyActionButtons}>
            {actionButton(
              data.primaryActionLabel,
              data.primaryActionUrl,
              data.id,
              data,
            )}
            {actionButton(
              data.secondaryActionLabel,
              data.secondaryActionUrl,
              data.id,
              data,
            )}
          </div>
        </div>
      </AccordionTab>
    )
  }

  const onPageChange = (event) => {
    if (pageSize !== event.rows) {
      setPageSize(event.rows)
    }
    GetNotifications(event.page + 1, event.rows)
  }

  const handleSelectionChange = (e) => {
    const index = e.index
    if (index !== null) {
      const notification = notifications.items[index]
      if (notification.systemNotification) {
        //Prevent expansion
        return
      }
    }
    setActiveNotificationIndex(index)
  }

  return (
    <div>
      {/* Notifications dialog */}
      <Dialog
        visible={visible}
        onHide={() => setVisible(false)}
        header={
          <StandardPageHeader
            title="Notifications"
            refreshData={() => GetNotifications(1, pageSize)}
            overrideClassNames={"pageHeaderCustom"}
          />
        }
        className="custom_dialog"
        style={{
          width: "96vw",
          height: "96vh",
          maxWidth: "1400px",
        }}
      >
        <div style={{ padding: "5px" }}>
          <CustomFilter
            filters={notificationFilters}
            setFilters={setNotificationFilters}
            sorting={notificationSorting}
            setSorting={setNotificationSorting}
            applyFilters={() => RefreshNotifications()}
          >
            <Button
              label="Mark all read"
              icon="pi pi-list-check"
              className={classNames(noteStyle.allread_button)}
              onClick={() => setMarkAllReadConfirmed(true)}
              disabled={loading}
              visible={
                notifications?.items?.filter((n) => !n.read).length > 0 || false
              }
            />
          </CustomFilter>

          {loading ? (
            <LoaderMedium />
          ) : (
            <>
              {notifications && notifications.items.length > 0 ? (
                <>
                  <Accordion
                    className={noteStyle.noteAccordion}
                    activeIndex={activeNotificationIndex}
                    onTabChange={handleSelectionChange}
                    style={{ marginTop: "0.6rem", marginBottom: "0.6rem" }}
                  >
                    {notifications
                      ? notifications.items.map((x) => {
                          return itemTemplate(x)
                        })
                      : null}
                  </Accordion>
                  <Paginator
                    first={notifications.pageSize * (notifications.pageNum - 1)}
                    rows={notifications.pageSize}
                    totalRecords={notifications.numTotal}
                    onPageChange={onPageChange}
                    style={{
                      borderColor: "var(--branded-purple-border) !important",
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                </>
              ) : (
                <div style={{ marginTop: "0.6rem", marginBottom: "0.6rem" }}>
                  <NoData
                    icon={
                      <BoxMessageIcon
                        style={{
                          padding: "8px",
                          width: "100%",
                          height: "100%",
                        }}
                      />
                    }
                    title="No Notifications"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </Dialog>

      {/* Mark all read dialog */}
      <Dialog
        visible={markAllReadConfirmed}
        onHide={() => setMarkAllReadConfirmed(false)}
        header="Please confirm"
        className="custom_dialog"
        style={{ width: "350px" }}
      >
        <p>Are you sure you want to mark all unread notifications as read?</p>
        <div className={classNames(noteStyle.allread_confirm_container)}>
          <Button
            label="Yes"
            className={classNames(noteStyle.allread_confirm_yes)}
            onClick={() => {
              setMarkAllReadConfirmed(false)
              markAllAsRead()
            }}
          />
          <Button
            label="No"
            className={classNames(noteStyle.allread_confirm_no)}
            onClick={() => setMarkAllReadConfirmed(false)}
          />
        </div>
      </Dialog>
    </div>
  )
}

export default NotificationsDialog
